import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, ChevronRight, Building2 } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post, patch, del } from '@/lib/api';
import { useToast } from '@theonefamily/ui';

interface Department {
  id: string;
  name: string;
  department_code?: string;
  parent_id?: string;
  head_id?: string;
  head_name?: string;
  employee_count: number;
  budget?: number;
  budget_currency?: string;
  cost_center?: string;
  active: boolean;
}

const EMPTY_FORM = {
  name: '',
  department_code: '',
  parent_id: '',
  head_id: '',
  head_name: '',
  budget: '' as string | number,
  budget_currency: 'USD',
  cost_center: '',
  active: true,
};

export function DepartmentsAdminPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { data, refetch } = useApi<{ departments: Department[] }>('admin/departments');

  const departments = data?.departments ?? [];

  // Build hierarchy tree
  const rootDepts = departments.filter(d => !d.parent_id);
  const childrenOf = (parentId: string) => departments.filter(d => d.parent_id === parentId);

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(d: Department) {
    setForm({
      name: d.name,
      department_code: d.department_code ?? '',
      parent_id: d.parent_id ?? '',
      head_id: d.head_id ?? '',
      head_name: d.head_name ?? '',
      budget: d.budget ?? '',
      budget_currency: d.budget_currency ?? 'USD',
      cost_center: d.cost_center ?? '',
      active: d.active,
    });
    setEditingId(d.id);
    setShowForm(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        department_code: form.department_code || undefined,
        parent_id: form.parent_id || undefined,
        head_name: form.head_name || undefined,
        budget: form.budget !== '' ? Number(form.budget) : undefined,
        budget_currency: form.budget_currency,
        cost_center: form.cost_center || undefined,
        active: form.active,
      };
      if (editingId) {
        await patch(`admin/departments/${editingId}`, payload);
      } else {
        await post('admin/departments', payload);
      }
      setShowForm(false);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete department "${name}"? This will fail if employees are assigned.`)) return;
    try {
      await del(`admin/departments/${id}`);
      refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Cannot delete department with active employees.');
    }
  }

  function DeptRow({ dept, depth = 0 }: { dept: Department; depth?: number }) {
    const children = childrenOf(dept.id);
    return (
      <>
        <Card key={dept.id}>
          <div className="flex items-start justify-between" style={{ paddingLeft: depth * 16 }}>
            <div className="flex items-start gap-3">
              {depth > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />}
              <Building2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{dept.name}</span>
                  {dept.department_code && <span className="text-xs text-muted-foreground">({dept.department_code})</span>}
                  {!dept.active && <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-muted-foreground">Inactive</span>}
                </div>
                <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                  <span>{dept.employee_count} employee{dept.employee_count !== 1 ? 's' : ''}</span>
                  {dept.head_name && <span>Head: {dept.head_name}</span>}
                  {dept.cost_center && <span>CC: {dept.cost_center}</span>}
                  {dept.budget && <span>Budget: {new Intl.NumberFormat('en-US', { style: 'currency', currency: dept.budget_currency ?? 'USD', maximumFractionDigits: 0 }).format(dept.budget)}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => openEdit(dept)} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(dept.id, dept.name)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        </Card>
        {children.map(child => <DeptRow key={child.id} dept={child} depth={depth + 1} />)}
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground">Departments</h2>
        <button onClick={openCreate} className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> New Department
        </button>
      </div>

      {showForm && (
        <Card className="border border-amber-500/30">
          <h3 className="text-sm font-semibold text-foreground mb-4">{editingId ? 'Edit Department' : 'New Department'}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Department Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Engineering" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Department Code</label>
              <input value={form.department_code} onChange={e => setForm(f => ({ ...f, department_code: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="ENG" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Parent Department</label>
              <select value={form.parent_id} onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="">None (top-level)</option>
                {departments.filter(d => d.id !== editingId).map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Manager Name</label>
              <input value={form.head_name} onChange={e => setForm(f => ({ ...f, head_name: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Jane Smith" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Annual Budget</label>
              <input type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="500000" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Budget Currency</label>
              <select value={form.budget_currency} onChange={e => setForm(f => ({ ...f, budget_currency: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                {['USD', 'EUR', 'GBP', 'CAD', 'AUD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Cost Center Code</label>
              <input value={form.cost_center} onChange={e => setForm(f => ({ ...f, cost_center: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="CC-001" />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="rounded" />
                Active
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving || !form.name} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black text-sm font-medium rounded-lg transition-colors">
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-foreground text-sm rounded-lg transition-colors">Cancel</button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {departments.length === 0 && !showForm && (
          <div className="text-center py-12 text-muted-foreground text-sm">No departments yet. Create one to get started.</div>
        )}
        {rootDepts.map(dept => <DeptRow key={dept.id} dept={dept} />)}
      </div>
    </div>
  );
}
